<script setup>

import {usePortalContentAndDesignStore} from "../../../../../stores/partners/portalContentAndDesignStore";
import {computed} from "vue";
import {useRoute} from "vue-router";
import EditableRouterLink from "../../../portal-editable-fields/EditableRouterLink.vue";

const props = defineProps({
    layoutType: {
        type: String,
        default: 'desktop',
    }
})

const portalContentAndDesignStore = usePortalContentAndDesignStore()

const navigationColor = computed(() => portalContentAndDesignStore.portalLayoutSettings.portal_layout_navigation_color);
const navigationLinks = computed(() => portalContentAndDesignStore.portalLayoutSettings.portal_layout_navigation_links);
const corners = computed(() => portalContentAndDesignStore.portalLayoutSettings.portal_layout_layout_corners);

// In the vertical left-sidebar layout, full-width pills need their wrapping
// container to fill the sidebar too (desktop containers are otherwise content-width).
const linkContainerClasses = computed(() => {
    const isSidebar = portalContentAndDesignStore.portalLayoutSettings.portal_layout === 'three';
    return (props.layoutType === 'mobile' || (isSidebar && navigationLinks.value === 'pills')) ? 'flex w-full' : 'flex';
});


const currentRoute = useRoute();

const navigationLinkClasses = (routeName) => {
    const isActive = currentRoute.name === routeName;

    const isMobile = props.layoutType === 'mobile';
    const isLayoutTwo = portalContentAndDesignStore.portalLayoutSettings.portal_layout === 'two';
    const isLayoutOne = portalContentAndDesignStore.portalLayoutSettings.portal_layout === 'one';
    // Layout 'three' is the vertical left-sidebar nav; pills there should fill the
    // sidebar width (like the border style) instead of only wrapping their label.
    const isSidebarPills = navigationLinks.value === 'pills' && !isMobile && !isLayoutOne && !isLayoutTwo;

    const classes = isMobile
        ? ['flex', 'w-full', 'py-3', 'px-3', 'text-sm', 'items-center', 'space-x-2.5']
        : ['font-medium', 'flex', 'space-x-3.5', 'group', 'items-center', 'px-3', 'py-2.5', 'text-sm'];

    const borderClass = isMobile || isLayoutTwo || isLayoutOne ? 'border-b-2' : 'border-r-2 w-full';

    if (isActive) {
        if (navigationLinks.value === 'pills') {
            classes.push(corners.value);
            if (isSidebarPills) classes.push('w-full');
            if (navigationColor.value === 'brand') {
                // Nav bar already IS the brand color, so the active pill needs a
                // contrasting white fill with brand-colored text.
                classes.push(portalContentAndDesignStore.getTextPrimaryColorClass, 'bg-white');
            } else {
                // black / white / gray nav: a filled accent (primary) pill with white
                // text reads clearly as the active tab. The previous black-nav branch
                // used a white pill + the theme text color, which is invisible on dark
                // themes where that text color is light.
                classes.push('text-white', portalContentAndDesignStore.getNavBackgroundColorClass);
            }
        } else if (navigationLinks.value === 'border') {
            classes.push(borderClass);
            if (navigationColor.value === 'brand') {
                classes.push('text-white', 'border-opacity-100', 'border-white');
            } else if (navigationColor.value === 'black') {
                classes.push('text-white', 'border-opacity-100', portalContentAndDesignStore.getBorderPrimaryColorClass);
            } else {
                classes.push(
                    portalContentAndDesignStore.getTextColorClass,
                    'border-opacity-100',
                    'text-opacity-100',
                    portalContentAndDesignStore.getBorderPrimaryColorClass
                );
            }
        } else {
            classes.push(
                navigationColor.value === 'brand' || navigationColor.value === 'black'
                    ? 'text-white'
                    : portalContentAndDesignStore.getTextPrimaryColorClass
            );
        }
    } else {
        if (navigationLinks.value === 'pills') {
            classes.push(corners.value, 'text-opacity-80', 'hover:text-opacity-100');
            if (isSidebarPills) classes.push('w-full');
            if (navigationColor.value === 'brand' || navigationColor.value === 'black') {
                classes.push('text-white', 'hover:bg-white', 'hover:bg-opacity-10');
            } else {
                classes.push(
                    portalContentAndDesignStore.getTextColorClass,
                    `hover:${portalContentAndDesignStore.getTextPrimaryColorClass}`,
                    `hover:${portalContentAndDesignStore.getNavBackgroundColorClass}`,
                    'hover:bg-opacity-5'
                );
            }
        } else if (navigationLinks.value === 'border') {
            classes.push(borderClass, 'border-opacity-0', 'hover:text-opacity-100');

            const isDarkLayout = navigationColor.value === 'brand' || navigationColor.value === 'black';

            if (isLayoutOne) {
                classes.push(
                    isDarkLayout ? 'text-white text-opacity-80' : `${portalContentAndDesignStore.getTextColorClass} text-opacity-70`,
                    portalContentAndDesignStore.getBorderPrimaryColorClass,
                    'hover:border-opacity-40',
                    isDarkLayout ? 'hover:border-white' : 'hover:border-gray-200'
                );
            } else if (isLayoutTwo) {
                classes.push(
                    isDarkLayout ? 'text-white text-opacity-70' : `${portalContentAndDesignStore.getTextColorClass} text-opacity-70`,
                    portalContentAndDesignStore.getBorderPrimaryColorClass,
                    'hover:border-opacity-40',
                    isDarkLayout ? 'hover:border-white' : 'hover:border-gray-200'
                );
            } else {
                // layout 'three' or default
                classes.push(
                    isDarkLayout ? 'text-white text-opacity-70' : `${portalContentAndDesignStore.getTextColorClass} text-opacity-70`,
                    portalContentAndDesignStore.getBorderPrimaryColorClass,
                    'hover:border-opacity-40',
                    isDarkLayout ? 'hover:border-white' : 'hover:border-gray-200'
                );
            }
        } else {
            classes.push(isLayoutTwo ? 'text-opacity-70' : 'text-opacity-75', 'hover:text-opacity-90');
            if (navigationColor.value === 'brand' || navigationColor.value === 'black') {
                classes.push('text-white');
            } else {
                classes.push(isLayoutOne ? 'text-opacity-70' : 'text-opacity-75', 'hover:text-opacity-90');

                if (navigationColor.value === 'brand' || navigationColor.value === 'black') {
                    classes.push('text-white');
                } else {
                    classes.push(
                        portalContentAndDesignStore.getTextColorClass,
                        `hover:${portalContentAndDesignStore.getTextPrimaryColorClass}`
                    );
                }
            }
        }
    }

    return classes.join(' ');
};


</script>

<template>
    <editable-router-link
        v-for="menuItem in portalContentAndDesignStore.menu"
        :key="menuItem['title']"
        :id="`menu-item-${menuItem['title']}`"
        :container-classes="linkContainerClasses"
        edit-input-container-classes="flex items-center"
        :link-classes="navigationLinkClasses(menuItem['name'])"
        :to="menuItem['path']"
        v-model:label="portalContentAndDesignStore.translationSettings[menuItem['store_key']]"
    >
        <template #custom-content>
            <template v-if="portalContentAndDesignStore.portalLayoutSettings.portal_layout_icons === 'outline'">
                <svg v-if="menuItem['store_key'] === 'portal_translation_navigation_dashboard'"
                     class="w-4 h-4" xmlns="http://www.w3.org/2000/svg"
                     viewBox="0 0 24 24"
                     width="24" height="24" fill="currentColor">
                    <path fill="none" d="M0 0h24v24H0z"/>
                    <path
                        d="M19 19V9.799l-7-5.522-7 5.522V19h14zm2 1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.314a1 1 0 0 1 .38-.785l8-6.311a1 1 0 0 1 1.24 0l8 6.31a1 1 0 0 1 .38.786V20zM7 12h2a3 3 0 0 0 6 0h2a5 5 0 0 1-10 0z"/>
                </svg>
                <svg v-else-if="menuItem['store_key'] === 'portal_translation_navigation_referrals'"
                     class="w-4 h-4" xmlns="http://www.w3.org/2000/svg"
                     viewBox="0 0 24 24"
                     width="24" height="24" fill="currentColor">
                    <path fill="none" d="M0 0h24v24H0z"/>
                    <path
                        d="M12 17c3.662 0 6.865 1.575 8.607 3.925l-1.842.871C17.347 20.116 14.847 19 12 19c-2.847 0-5.347 1.116-6.765 2.796l-1.841-.872C5.136 18.574 8.338 17 12 17zm0-15a5 5 0 0 1 5 5v3a5 5 0 0 1-4.783 4.995L12 15a5 5 0 0 1-5-5V7a5 5 0 0 1 4.783-4.995L12 2zm0 2a3 3 0 0 0-2.995 2.824L9 7v3a3 3 0 0 0 5.995.176L15 10V7a3 3 0 0 0-3-3z"/>
                </svg>
                <svg v-else-if="menuItem['store_key'] === 'portal_translation_navigation_leads'"
                     class="w-4 h-4" xmlns="http://www.w3.org/2000/svg"
                     viewBox="0 0 24 24"
                     width="24" height="24" fill="currentColor">
                    <path fill="none" d="M0 0h24v24H0z"/>
                    <path
                        d="M12 17c3.662 0 6.865 1.575 8.607 3.925l-1.842.871C17.347 20.116 14.847 19 12 19c-2.847 0-5.347 1.116-6.765 2.796l-1.841-.872C5.136 18.574 8.338 17 12 17zm0-15a5 5 0 0 1 5 5v3a5 5 0 0 1-4.783 4.995L12 15a5 5 0 0 1-5-5V7a5 5 0 0 1 4.783-4.995L12 2zm0 2a3 3 0 0 0-2.995 2.824L9 7v3a3 3 0 0 0 5.995.176L15 10V7a3 3 0 0 0-3-3z"/>
                </svg>
                <svg v-else-if="menuItem['store_key'] === 'portal_translation_navigation_resources'"
                     class="w-4 h-4" xmlns="http://www.w3.org/2000/svg"
                     viewBox="0 0 24 24"
                     width="24" height="24" fill="currentColor">
                    <path fill="none" d="M0 0h24v24H0z"/>
                    <path
                        d="M4.828 21l-.02.02-.021-.02H2.992A.993.993 0 0 1 2 20.007V3.993A1 1 0 0 1 2.992 3h18.016c.548 0 .992.445.992.993v16.014a1 1 0 0 1-.992.993H4.828zM20 15V5H4v14L14 9l6 6zm0 2.828l-6-6L6.828 19H20v-1.172zM8 11a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"/>
                </svg>
                <svg v-if="menuItem['store_key'] === 'portal_translation_navigation_payouts'"
                     class="w-4 h-4" xmlns="http://www.w3.org/2000/svg"
                     viewBox="0 0 24 24"
                     width="24" height="24" fill="currentColor">
                    <path fill="none" d="M0 0h24v24H0z"/>
                    <path
                        d="M3 3h18a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm17 8H4v8h16v-8zm0-2V5H4v4h16zm-6 6h4v2h-4v-2z"/>
                </svg>
                <svg v-else-if="menuItem['store_key'] === 'portal_translation_navigation_reporting'"
                     class="w-4 h-4" fill="currentColor"
                     xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24"
                     height="24">
                    <path fill="none" d="M0 0H24V24H0z"/>
                    <path
                        d="M5 3v16h16v2H3V3h2zm15.293 3.293l1.414 1.414L16 13.414l-3-2.999-4.293 4.292-1.414-1.414L13 7.586l3 2.999 4.293-4.292z"/>
                </svg>
                <svg v-else-if="menuItem['store_key'] === 'portal_translation_navigation_messages'"
                     class="w-4 h-4" xmlns="http://www.w3.org/2000/svg"
                     viewBox="0 0 24 24"
                     width="24" height="24" fill="currentColor">
                    <path fill="none" d="M0 0h24v24H0z"/>
                    <path
                        d="M6.455 19L2 22.5V4a1 1 0 0 1 1-1h18a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H6.455zM5.763 17H20V5H4v13.385L5.763 17zM11 10h2v2h-2v-2zm-4 0h2v2H7v-2zm8 0h2v2h-2v-2z"/>
                </svg>
            </template>
            <template v-if="portalContentAndDesignStore.portalLayoutSettings.portal_layout_icons === 'bold'">
                <svg v-if="menuItem['store_key'] === 'portal_translation_navigation_dashboard'"
                     xmlns="http://www.w3.org/2000/svg" class="w-4 h-4"
                     viewBox="0 0 24 24"
                     width="24" height="24" fill="currentColor">
                    <path fill="none" d="M0 0h24v24H0z"/>
                    <path
                        d="M21 20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.314a1 1 0 0 1 .38-.785l8-6.311a1 1 0 0 1 1.24 0l8 6.31a1 1 0 0 1 .38.786V20zM7 12a5 5 0 0 0 10 0h-2a3 3 0 0 1-6 0H7z"/>
                </svg>

                <svg v-else-if="menuItem['store_key'] === 'portal_translation_navigation_referrals'"
                     xmlns="http://www.w3.org/2000/svg" class="w-4 h-4"
                     viewBox="0 0 24 24"
                     width="24" height="24" fill="currentColor">
                    <path fill="none" d="M0 0h24v24H0z"/>
                    <path
                        d="M12 17c3.662 0 6.865 1.575 8.607 3.925l-1.842.871C17.347 20.116 14.847 19 12 19c-2.847 0-5.347 1.116-6.765 2.796l-1.841-.872C5.136 18.574 8.338 17 12 17zm0-15a5 5 0 0 1 5 5v3a5 5 0 0 1-10 0V7a5 5 0 0 1 5-5z"/>
                </svg>

                <svg v-else-if="menuItem['store_key'] === 'portal_translation_navigation_leads'"
                     xmlns="http://www.w3.org/2000/svg" class="w-4 h-4"
                     viewBox="0 0 24 24"
                     width="24" height="24" fill="currentColor">
                    <path fill="none" d="M0 0h24v24H0z"/>
                    <path
                        d="M12 17c3.662 0 6.865 1.575 8.607 3.925l-1.842.871C17.347 20.116 14.847 19 12 19c-2.847 0-5.347 1.116-6.765 2.796l-1.841-.872C5.136 18.574 8.338 17 12 17zm0-15a5 5 0 0 1 5 5v3a5 5 0 0 1-10 0V7a5 5 0 0 1 5-5z"/>
                </svg>

                <svg v-else-if="menuItem['store_key'] === 'portal_translation_navigation_resources'"
                     xmlns="http://www.w3.org/2000/svg" class="w-4 h-4"
                     viewBox="0 0 24 24"
                     width="24" height="24" fill="currentColor">
                    <path fill="none" d="M0 0h24v24H0z"/>
                    <path
                        d="M20 5H4v14l9.292-9.294a1 1 0 0 1 1.414 0L20 15.01V5zM2 3.993A1 1 0 0 1 2.992 3h18.016c.548 0 .992.445.992.993v16.014a1 1 0 0 1-.992.993H2.992A.993.993 0 0 1 2 20.007V3.993zM8 11a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"/>
                </svg>

                <svg v-if="menuItem['store_key'] === 'portal_translation_navigation_payouts'"
                     xmlns="http://www.w3.org/2000/svg" class="w-4 h-4"
                     viewBox="0 0 24 24"
                     width="24" height="24" fill="currentColor">
                    <path fill="none" d="M0 0h24v24H0z"/>
                    <path
                        d="M22 10v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V10h20zm0-2H2V4a1 1 0 0 1 1-1h18a1 1 0 0 1 1 1v4zm-7 8v2h4v-2h-4z"/>
                </svg>

                <svg v-else-if="menuItem['store_key'] === 'portal_translation_navigation_reporting'"
                     class="w-4 h-4" fill="currentColor"
                     xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24"
                     height="24">
                    <path fill="none" d="M0 0H24V24H0z"/>
                    <path
                        d="M5 3v16h16v2H3V3h2zm14.94 2.94l2.12 2.12L16 14.122l-3-3-3.94 3.94-2.12-2.122L13 6.88l3 3 3.94-3.94z"/>
                </svg>
                <svg v-else-if="menuItem['store_key'] === 'portal_translation_navigation_messages'"
                     xmlns="http://www.w3.org/2000/svg" class="w-4 h-4"
                     viewBox="0 0 24 24"
                     width="24" height="24" fill="currentColor">
                    <path fill="none" d="M0 0h24v24H0z"/>
                    <path
                        d="M6.455 19L2 22.5V4a1 1 0 0 1 1-1h18a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H6.455zM5.763 17H20V5H4v13.385L5.763 17zM11 10h2v2h-2v-2zm-4 0h2v2H7v-2zm8 0h2v2h-2v-2z"/>
                </svg>
            </template>

            <span>{{ portalContentAndDesignStore.translationSettings[menuItem['store_key']] }}</span>
        </template>

    </editable-router-link>
</template>

<style scoped>

</style>
